/*
 * Typed wrappers over the goserver /v2/sales_team endpoints. Every response
 * shape this page and the sales task list depend on is declared here, so a
 * contract change is a one-file fix.
 */

import {
  delete_api as deleteApi,
  get_api as getApi,
  post_api as postApi,
  put_api as putApi
} from '../../../redux/api_funcs';
import { get_api_url as getApiUrl } from '../../../utils/urls';

const BASE = '/v2/sales_team';

export type RepLevel = 'L1' | 'L2' | 'L3';
export type TeamLevel = RepLevel | 'MANAGER';
export type Weekday = 'MON' | 'TUE' | 'WED' | 'THU' | 'FRI' | 'SAT' | 'SUN';
export type MemberStatus = 'ACTIVE' | 'LEFT';

export const WEEKDAYS: Weekday[] = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
export const TEAM_LEVELS: TeamLevel[] = ['L1', 'L2', 'L3', 'MANAGER'];
export const MEMBER_STATUSES: MemberStatus[] = ['ACTIVE', 'LEFT'];

// GET /v2/sales_team/me — 200 for non-members too (level null).
export interface SalesMe {
  ops_uuid?: string;
  level?: TeamLevel | null;
  can_assign?: boolean;
  // Absent on servers predating it.
  can_claim?: boolean;
  can_edit_team?: boolean;
  // Advisor assign/reassign/end: an active roster sales MANAGER only (SUPER_USER alone is view-only).
  can_write_advisors?: boolean;
  on_shift_now?: boolean;
  on_leave_today?: boolean;
  claim_idle_minutes?: number;
  // Team call window, HH:MM in the user's local time.
  call_window_start?: string;
  call_window_end?: string;
}

// Dates are YYYY-MM-DD, inclusive on both ends.
export interface TeamLeave {
  id: number;
  from_date: string;
  to_date: string;
  note?: string;
}

export interface TeamMember {
  ops_uuid: string;
  name: string;
  email?: string;
  configured: boolean;
  level: TeamLevel | null;
  // 'HH:mm' IST; shift_end < shift_start means the shift crosses midnight.
  shift_start: string | null;
  shift_end: string | null;
  weekly_off: Weekday[];
  status: MemberStatus;
  leaves: TeamLeave[];
  next_leave: TeamLeave | null;
  on_leave_today: boolean;
  on_shift_now: boolean;
  open_claim_uuid: string | null;
  owned_students: number;
}

export interface TeamRoster {
  can_edit: boolean;
  server_now_iso?: string;
  members: TeamMember[];
}

export interface RepProfileUpdate {
  level: TeamLevel;
  shift_start: string | null;
  shift_end: string | null;
  weekly_off: Weekday[];
  status: MemberStatus;
}

// The edit form before validation: the level may still be unset.
export type RepEditDraft = Omit<RepProfileUpdate, 'level'> & { level: TeamLevel | null };

// PUT /v2/sales_team/:ops_uuid — released_claims > 0 when the save marked the rep LEFT.
export interface RepUpdateResult {
  member: TeamMember;
  released_claims: number;
}

export interface NewLeave {
  from_date: string;
  to_date: string;
  note?: string;
}

interface Envelope<T> {
  data?: { data?: T };
}

export interface ApiError {
  response?: { status?: number; data?: { reason?: string; message?: string } };
}

const unwrap = <T>(res: Envelope<T> | null | undefined): T | undefined => res?.data?.data;

const member_url = (ops_uuid: string): string => `${BASE}/${encodeURIComponent(ops_uuid)}`;

export const fetch_me = (): Promise<SalesMe> =>
  getApi(getApiUrl(`${BASE}/me`), true).then((res: Envelope<SalesMe>) => unwrap(res) || {});

export const fetch_roster = (): Promise<TeamRoster> =>
  getApi(getApiUrl(BASE), true).then((res: Envelope<TeamRoster>) => {
    const d = unwrap(res);
    return {
      can_edit: !!d?.can_edit,
      server_now_iso: d?.server_now_iso,
      members: Array.isArray(d?.members) ? d.members : []
    };
  });

export const update_rep = (ops_uuid: string, body: RepProfileUpdate): Promise<RepUpdateResult> =>
  putApi(getApiUrl(member_url(ops_uuid)), body, true).then(
    (res: Envelope<{ member: TeamMember; released_claims?: number }>) => {
      const d = unwrap(res);
      return {
        member: d?.member as TeamMember,
        released_claims: Number(d?.released_claims) || 0
      };
    }
  );

// Resolves undefined when the response carried no leave.
export const add_leave = (ops_uuid: string, body: NewLeave): Promise<TeamLeave | undefined> =>
  postApi(getApiUrl(`${member_url(ops_uuid)}/leaves`), body, true).then(
    (res: Envelope<TeamLeave>) => unwrap(res)
  );

export const delete_leave = (ops_uuid: string, id: number): Promise<void> =>
  deleteApi(
    getApiUrl(`${member_url(ops_uuid)}/leaves/${encodeURIComponent(String(id))}`),
    true
  ).then(() => undefined);

export const api_error_reason = (e: unknown, fallback: string): string => {
  const data = (e as ApiError | null)?.response?.data;
  return data?.reason || data?.message || fallback;
};
